import { useEffect, useRef, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { LifeBuoy, SendHorizontal } from "lucide-react"
import { authClient } from "@/lib/auth-client"
import { trpc } from "@/lib/trpc-client"
import {
  supportMessagesKey,
  useSupportBroadcast,
} from "@/lib/hooks/use-support-broadcast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Logo } from "@/components/Logo"
import { getDateLabel, formatTime, isSameDay } from "@/lib/support-format"
import type { SupportMessage } from "@/db/schema"

const TOKEN_STORAGE_KEY = `support_token`

function readStoredToken(): string | undefined {
  if (typeof localStorage === `undefined`) return undefined
  return localStorage.getItem(TOKEN_STORAGE_KEY) ?? undefined
}

/**
 * Visitor-facing live support chat. Works for both logged-in users (scoped to
 * their account) and anonymous visitors (scoped to a guest token persisted in
 * localStorage). The admin replies from /admin/support; replies arrive live via
 * the support Realtime Broadcast topic, and an anonymous visitor who leaves an
 * email also gets notified by email.
 */
export function SupportChat() {
  const { data: session, isPending: sessionPending } = authClient.useSession()
  const isAuthed = !!session?.user
  const queryClient = useQueryClient()

  const [conversationId, setConversationId] = useState<string>()
  const [guestToken, setGuestToken] = useState<string | undefined>(
    readStoredToken
  )
  const [draft, setDraft] = useState(``)
  const [email, setEmail] = useState(``)
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Open (or resume) the conversation once we know whether the visitor is authed.
  useEffect(() => {
    if (sessionPending) return
    let cancelled = false
    trpc.support.openConversation
      .mutate({ guestToken: isAuthed ? undefined : readStoredToken() })
      .then((res) => {
        if (cancelled) return
        setConversationId(res.conversation.id)
        if (res.guestToken) {
          setGuestToken(res.guestToken)
          localStorage.setItem(TOKEN_STORAGE_KEY, res.guestToken)
        }
      })
      .catch(() => {
        /* surfaced via the disabled composer below */
      })
    return () => {
      cancelled = true
    }
  }, [sessionPending, isAuthed])

  const messagesQuery = useQuery({
    queryKey: supportMessagesKey(conversationId),
    queryFn: () =>
      trpc.support.getMessages.query({
        conversationId: conversationId as string,
        guestToken: isAuthed ? undefined : guestToken,
      }),
    enabled: !!conversationId,
  })

  useSupportBroadcast(
    conversationId ? `support:${conversationId}` : undefined,
    [supportMessagesKey(conversationId)]
  )

  const messages = messagesQuery.data ?? []

  // Keep the latest message in view as the thread grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages.length])

  // Auto-grow the composer up to a few lines.
  function resizeTextarea() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = `auto`
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`
  }
  useEffect(resizeTextarea, [draft])

  async function handleSend() {
    const body = draft.trim()
    if (!body || !conversationId || sending) return
    setSending(true)
    const key = supportMessagesKey(conversationId)

    // Optimistic append so the message shows immediately; reconciled on refetch.
    const optimistic: SupportMessage = {
      id: `optimistic-${Date.now()}`,
      db_id: 0,
      conversation_id: conversationId,
      sender: `user`,
      sender_user_id: session?.user.id ?? null,
      body,
      created_at: new Date(),
    }
    queryClient.setQueryData<SupportMessage[]>(key, (prev) => [
      ...(prev ?? []),
      optimistic,
    ])
    setDraft(``)

    try {
      // Capture an anonymous visitor's email (optional) so we can email replies.
      if (!isAuthed && email.trim()) {
        await trpc.support.openConversation.mutate({
          guestToken,
          guestEmail: email.trim(),
        })
      }
      await trpc.support.sendMessage.mutate({
        conversationId,
        guestToken: isAuthed ? undefined : guestToken,
        body,
      })
    } catch {
      // Roll the optimistic message back and restore the draft on failure.
      queryClient.setQueryData<SupportMessage[]>(key, (prev) =>
        (prev ?? []).filter((m) => m.id !== optimistic.id)
      )
      setDraft(body)
    } finally {
      setSending(false)
      queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const connecting = !conversationId

  return (
    <div className="flex flex-col h-[70vh] min-h-[28rem] rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border bg-card/80 backdrop-blur">
        <div className="relative">
          <div className="flex items-center justify-center h-9 w-9 rounded-full bg-muted">
            <Logo alt="ChicTravel" className="h-6 w-6 object-contain" />
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-card" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-tight">
            ChicTravel Support
          </div>
          <div className="text-xs text-muted-foreground leading-tight">
            {connecting ? `Connecting…` : `We typically reply within a few hours`}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-5 bg-muted/20"
      >
        {connecting ? (
          <ConnectingState />
        ) : messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-1">
            {messages.map((m, i) => {
              const prev = messages[i - 1]
              const showDate = !isSameDay(prev?.created_at, m.created_at)
              const isUser = m.sender === `user`
              const startsRun = !prev || prev.sender !== m.sender || showDate
              const optimistic = String(m.id).startsWith(`optimistic-`)
              return (
                <div key={m.id}>
                  {showDate && (
                    <div className="flex items-center justify-center my-4">
                      <span className="text-[11px] font-medium text-muted-foreground bg-background/80 border border-border rounded-full px-3 py-1">
                        {getDateLabel(m.created_at)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex items-end gap-2 ${
                      isUser ? `justify-end` : `justify-start`
                    } ${startsRun ? `mt-3` : `mt-0.5`}`}
                  >
                    {!isUser && (
                      <div className="w-6 shrink-0">
                        {startsRun && (
                          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-muted">
                            <Logo
                              alt="Support"
                              className="h-4 w-4 object-contain"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <div
                      title={formatTime(m.created_at)}
                      className={`group max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
                        isUser
                          ? `bg-primary text-primary-foreground rounded-br-md`
                          : `bg-background text-foreground border border-border rounded-bl-md`
                      } ${optimistic ? `opacity-60` : ``}`}
                    >
                      {m.body}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-border p-3 space-y-2 bg-card">
        {!isAuthed && (
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email (optional — so we can reply by email too)"
            className="h-9 text-sm bg-muted/40 border-transparent focus-visible:bg-background"
          />
        )}
        <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring/40 transition-shadow">
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === `Enter` && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="Type your message…"
            rows={1}
            className="flex-1 resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 px-1.5 py-1 min-h-0 max-h-[140px]"
            disabled={connecting}
          />
          <Button
            type="button"
            size="icon"
            onClick={handleSend}
            disabled={connecting || !draft.trim() || sending}
            className="rounded-lg shrink-0"
            aria-label="Send message"
          >
            <SendHorizontal className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center">
          Press <kbd className="font-sans font-medium">Enter</kbd> to send,
          {` `}
          <kbd className="font-sans font-medium">Shift+Enter</kbd> for a new line
        </p>
      </div>
    </div>
  )
}

function ConnectingState() {
  return (
    <div className="space-y-3">
      <div className="flex justify-start">
        <div className="h-9 w-44 rounded-2xl rounded-bl-md bg-background border border-border animate-pulse" />
      </div>
      <div className="flex justify-end">
        <div className="h-9 w-32 rounded-2xl rounded-br-md bg-muted animate-pulse" />
      </div>
      <div className="flex justify-start">
        <div className="h-9 w-52 rounded-2xl rounded-bl-md bg-background border border-border animate-pulse" />
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary mb-3">
        <LifeBuoy className="h-6 w-6" />
      </div>
      <h3 className="text-sm font-semibold">How can we help?</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-xs">
        Send us a message about planning, billing, guides, or your account — a
        real person will reply right here.
      </p>
    </div>
  )
}
